import * as Popover from "@radix-ui/react-popover";
import { Link } from "@tanstack/react-router";
import { Clock, LogIn, LogOut, Settings, ShieldCheck, UserRound } from "lucide-react";
import { useState, type ReactNode } from "react";

import { changeLocale, locales, useTranslation, type Locale } from "../../i18n";

export function UserMenu({
  session,
  isAdmin = false,
  pending = false,
  onSignOut,
}: Readonly<{
  session?: { user?: { name?: string | null; email?: string | null } | null } | null;
  isAdmin?: boolean;
  pending?: boolean;
  onSignOut(): void;
}>) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const locale = i18n.language;
  const labels: Record<Locale, string> = {
    "zh-CN": t("nav.languageZh"),
    en: t("nav.languageEn"),
  };
  const initial = (session?.user?.name || session?.user?.email || t("nav.login")).slice(0, 1).toUpperCase();

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button aria-label={t("nav.accountMenu")} aria-busy={pending} disabled={pending} className="avatar-button" type="button">
          {pending || !session ? <UserRound className="size-4" aria-hidden="true" /> : initial}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" className="user-menu" sideOffset={8}>
          {session ? (
            <>
              <div className="user-menu-identity">
                <span className="user-menu-kicker">{t("nav.signedInAs")}</span>
                <strong>{session.user?.name || session.user?.email}</strong>
                {session.user?.email && session.user?.name ? <span>{session.user.email}</span> : null}
              </div>
              <MenuLink onSelect={() => setOpen(false)} to="/requests">
                <Clock className="size-3.5" />
                {t("nav.requests")}
              </MenuLink>
              {isAdmin ? (
                <MenuLink onSelect={() => setOpen(false)} params={{ _splat: "overview" }} to="/admin/$">
                  <ShieldCheck className="size-3.5" />
                  {t("nav.admin")}
                </MenuLink>
              ) : null}
              <MenuLink onSelect={() => setOpen(false)} to="/settings/$" params={{ _splat: "api-keys" }}>
                <Settings className="size-3.5" />
                {t("nav.apiKeys")}
              </MenuLink>
              <button className="user-menu-item" onClick={() => { setOpen(false); onSignOut(); }} type="button">
                <LogOut className="size-3.5" />
                {t("nav.signOut")}
              </button>
            </>
          ) : (
            <MenuLink onSelect={() => setOpen(false)} to="/login">
              <LogIn className="size-3.5" />
              {t("nav.login")}
            </MenuLink>
          )}
          <div className="user-menu-section" role="group" aria-label={t("nav.language")}>
            <span className="user-menu-kicker">{t("nav.language")}</span>
            <div className="locale-switch">
              {locales.map((item) => (
                <button
                  aria-pressed={item === locale}
                  className={item === locale ? "active" : ""}
                  key={item}
                  onClick={() => void changeLocale(item)}
                  type="button"
                >
                  {labels[item]}
                </button>
              ))}
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function MenuLink({
  children,
  onSelect,
  ...props
}: Readonly<{ children: ReactNode; onSelect(): void; to: string; params?: Record<string, string> }>) {
  return (
    <Link className="user-menu-item" onClick={onSelect} {...props}>
      {children}
    </Link>
  );
}
